import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BehApiRoutes, behClient } from '../../../../api/beh-api';
import { useNotification } from '../../../../shared/notification/useNotification';
import { DatenmodelListResponse } from '../../../../api/models/Datamodels/datamodels';

export const useMissingDataModels = () => {
  const { notifyUserFromRequest } = useNotification();

  const {
    data: dataModels,
    isLoading: isLoadingDatamodels,
    error: dataModelsError
  } = useQuery({
    queryKey: [BehApiRoutes.datamodelsList()],
    queryFn: async () => {
      try {
        const result = await notifyUserFromRequest(() =>
          behClient.get<DatenmodelListResponse[]>(BehApiRoutes.datamodelsList())
        );
        return result;
      } catch (error) {
        console.error('dataModels API error:', error);
        throw error;
      }
    },
    select: (datamodelList) => {
      const groupedByIdentifier: Record<
        string,
        Array<DatenmodelListResponse>
      > = {};
      datamodelList.forEach((listEntry) => {
        if (!groupedByIdentifier[listEntry.identifier]) {
          groupedByIdentifier[listEntry.identifier] = [listEntry];
        } else {
          groupedByIdentifier[listEntry.identifier].push(listEntry);
        }
      });
      return groupedByIdentifier;
    }
  });

  // Get missing data models grouped by identifier - memoized to react to data changes
  const missingDataModels = useMemo((): Record<string, string[]> => {
    if (!dataModels) {
      return {};
    }

    const missingModels: Record<string, string[]> = {};

    Object.entries(dataModels).forEach(([identifier, models]) => {
      const missingVersions = models
        .filter((model) => !model.isDataModelProvided)
        .map((model) => model.datamodelVersion || 'Unknown Version');

      if (missingVersions.length > 0) {
        missingModels[identifier] = missingVersions;
      }
    });

    return missingModels;
  }, [dataModels]);

  // Check if there are any missing models
  const hasMissingModels = Object.keys(missingDataModels).length > 0;

  // Calculate total number of unique datamodel versions
  const totalModelsCount = useMemo((): number => {
    if (!dataModels) {
      return 0;
    }
    return Object.values(dataModels).flatMap((models) => models).length;
  }, [dataModels]);

  // Get available identifiers with warning indicators for missing models
  const getAvailableIdentifiers = () => {
    if (!dataModels) {
      return [];
    }

    return Object.keys(dataModels).map((identifier) => {
      const modelsForIdentifier = dataModels[identifier] || [];
      const hasMissingModelsForIdentifier = modelsForIdentifier.some(
        (model) => !model.isDataModelProvided
      );

      return {
        label: hasMissingModelsForIdentifier ? `⚠️ ${identifier}` : identifier,
        value: identifier
      };
    });
  };

  return {
    dataModels: dataModels || {},
    missingDataModels,
    hasMissingModels,
    totalModelsCount,
    isLoadingDatamodels,
    dataModelsError,
    getAvailableIdentifiers
  };
};
